import { useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router";
import { Loader2 } from "lucide-react";

import { AuthCard } from "@/components/auth/AuthCard";
import { PUBLIC_LINK } from "@/components/public/control-classes";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { useTenantPresentation } from "@/hooks/use-tenant-presentation";

const QUIET_LINK = `${PUBLIC_LINK} text-meta text-muted-foreground hover:text-primary hover:underline`;

/** Request a password-reset email (GoTrue `resetPasswordForEmail`). */
export function ForgotPasswordPage() {
  const { requestPasswordReset, verifyRecoveryCode } = useAuth();
  const { name, logoUrl } = useTenantPresentation();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const enteredCodeMode = searchParams.get("method") === "recovery-code";
  const [email, setEmail] = useState(() => {
    const candidate = (location.state as { email?: unknown } | null)?.email;
    return typeof candidate === "string" ? candidate : "";
  });
  const [code, setCode] = useState("");
  const [sent, setSent] = useState(enteredCodeMode);
  const [submitting, setSubmitting] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [sentEmail, setSentEmail] = useState<string | null>(null);
  const [requestError, setRequestError] = useState<string | null>(null);
  const [codeError, setCodeError] = useState<string | null>(null);

  const sendReset = async () => {
    const normalizedEmail = email.trim();
    if (!normalizedEmail) {
      setRequestError("Enter the email address you use for this academy.");
      return false;
    }
    setSubmitting(true);
    setSentEmail(null);
    setRequestError(null);
    // Always show the same confirmation regardless of whether the email exists
    // (no account enumeration).
    let error: string | null = null;
    try {
      ({ error } = await requestPasswordReset(normalizedEmail));
    } catch (caught) {
      error = caught instanceof Error ? caught.message : "Unknown auth error";
    } finally {
      setSubmitting(false);
    }
    if (error) {
      if (import.meta.env.DEV) console.error("Password-reset request failed", error);
      setRequestError(
        "We couldn't send recovery instructions. Wait a moment and try again.",
      );
      return false;
    }
    setSent(true);
    setSentEmail(normalizedEmail);
    setCode("");
    setCodeError(null);
    return true;
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await sendReset();
  };

  const onVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      setCodeError("Enter the email address that received the recovery code.");
      return;
    }
    const normalizedCode = code.replace(/\D/g, "");
    if (normalizedCode.length !== 6) {
      setCodeError("Enter the 6-digit recovery code from your email.");
      return;
    }

    setVerifying(true);
    setCodeError(null);
    let error: string | null = null;
    try {
      ({ error } = await verifyRecoveryCode(email.trim(), normalizedCode));
    } catch (caught) {
      error = caught instanceof Error ? caught.message : "Unknown auth error";
    } finally {
      setVerifying(false);
    }
    if (error) {
      setCodeError("That recovery code is invalid or has expired. Request a new code.");
      return;
    }
    navigate("/reset-password", { replace: true });
  };

  return (
    <AuthCard
      brandName={name}
      logoUrl={logoUrl}
      subtitle="Password recovery"
      eyebrow="Password recovery"
      heading={
        sent
          ? enteredCodeMode && !sentEmail
            ? "Enter your recovery code"
            : "Check your email"
          : "Reset your password"
      }
      lede={
        sent
          ? "Enter the email address and 6-digit code from your recovery email."
          : "Enter the email address you use for this academy."
      }
    >
      {sent ? (
        <form onSubmit={onVerifyCode} className="grid gap-4" noValidate>
          {sentEmail && (
            <p className="text-ui text-muted-foreground">
              If an account exists for <span className="font-medium text-foreground">{sentEmail}</span>,
              its recovery email is on the way.
            </p>
          )}
          {requestError && (
            <Alert variant="destructive">
              <AlertDescription>{requestError}</AlertDescription>
            </Alert>
          )}
          {codeError && (
            <Alert variant="destructive">
              <AlertDescription>{codeError}</AlertDescription>
            </Alert>
          )}
          <div className="grid gap-2">
            <Label htmlFor="recovery-email">Email</Label>
            <Input
              id="recovery-email"
              type="email"
              autoComplete="username"
              placeholder="you@imam-us.org"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="recovery-code">Recovery code</Label>
            <Input
              id="recovery-code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              placeholder="000000"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              required
              autoFocus
            />
          </div>
          <Button type="submit" className="w-full" disabled={verifying}>
            {verifying && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
            Verify code
          </Button>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <button
              type="button"
              disabled={submitting}
              className={QUIET_LINK}
              onClick={async () => {
                await sendReset();
              }}
            >
              {submitting
                ? "Sending…"
                : sentEmail
                  ? "Send another code"
                  : "Send a recovery email"}
            </button>
            <button
              type="button"
              className={QUIET_LINK}
              onClick={() => {
                setSent(false);
                setCode("");
                setCodeError(null);
                setRequestError(null);
                setSentEmail(null);
              }}
            >
              Use a different email
            </button>
          </div>
          <div className="text-center">
            <Link to="/sign-in" className={QUIET_LINK}>
              Back to sign in
            </Link>
          </div>
        </form>
      ) : (
        <form onSubmit={onSubmit} className="grid gap-4" noValidate>
          <p className="text-ui text-muted-foreground">
            We'll email a reset link and a 6-digit recovery code.
          </p>
          {requestError && (
            <Alert variant="destructive">
              <AlertDescription>{requestError}</AlertDescription>
            </Alert>
          )}
          <div className="grid gap-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              autoComplete="username"
              placeholder="you@imam-us.org"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <Button type="submit" className="w-full" disabled={submitting}>
            {submitting && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
            Send reset link
          </Button>
          <div className="text-center">
            <Link to="/sign-in" className={QUIET_LINK}>
              Back to sign in
            </Link>
          </div>
        </form>
      )}
    </AuthCard>
  );
}
